'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/home/primitives'
import {
  prefersReducedMotion,
  useInView,
  useTween,
} from '@/components/home/motion-hooks'

// Key figures that count up the first time the band scrolls into view.
// Server render and reduced motion show the final values.

const FACTS = [
  { label: 'Schweizer Hosting', to: 100, suffix: '%' },
  { label: 'Server-Standort', text: 'Zürich' },
  { label: 'Jahre Branchenerfahrung', to: 5, suffix: '+' },
  { label: 'Antwortzeit Support', to: 24, suffix: 'h' },
]

function Figure({ fact, run }) {
  const value = useTween(run ? fact.to : 0, 1400)
  return (
    <>
      {Math.round(value)}
      {fact.suffix}
    </>
  )
}

export function Facts() {
  const ref = useRef(null)
  const inView = useInView(ref, 0.5)
  const [mode, setMode] = useState('static')

  // Hide the numbers until the band is seen, then count once.
  useEffect(() => {
    if (prefersReducedMotion()) return
    setMode(current => (current === 'static' ? 'waiting' : current))
  }, [])
  useEffect(() => {
    if (inView && mode === 'waiting') setMode('counting')
  }, [inView, mode])

  return (
    <section
      ref={ref}
      aria-label="Otti One in Zahlen"
      className="border-y border-rule bg-surface"
    >
      <Container>
        <dl className="grid grid-cols-2 lg:grid-cols-4">
          {FACTS.map((fact, i) => (
            <div
              key={fact.label}
              className={cn(
                'relative flex flex-col gap-2 py-7',
                i % 2 === 1 && 'border-l border-rule pl-4',
                i >= 2 && 'border-t border-rule lg:border-t-0',
                i > 0 && 'lg:border-l lg:pl-6'
              )}
            >
              <dt className="font-mono text-xs text-signal">{fact.label}</dt>
              <dd className="flex items-center gap-2 text-[34px] font-[450] leading-none tabular-nums text-ink [font-stretch:110%] sm:text-[40px]">
                {fact.text ? (
                  <>
                    {fact.text}
                    <span aria-hidden className="relative flex h-2 w-2">
                      <span className="ping-soft absolute inset-0 rounded-full bg-signal" />
                      <span className="relative h-2 w-2 rounded-full bg-signal" />
                    </span>
                  </>
                ) : mode === 'static' ? (
                  `${fact.to}${fact.suffix}`
                ) : (
                  <Figure fact={fact} run={mode === 'counting'} />
                )}
              </dd>
              <span
                aria-hidden
                className={cn(
                  'absolute bottom-0 left-0 h-[2px] w-16 origin-left bg-signal transition-transform duration-1000 ease-out',
                  i % 2 === 1 && 'left-4',
                  i > 0 && 'lg:left-6',
                  mode === 'waiting' ? 'scale-x-0' : 'scale-x-100'
                )}
              />
            </div>
          ))}
        </dl>
      </Container>
    </section>
  )
}
